//src/components/Home/HomeFormActionsMenu.jsx

import React, {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Dialog,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';

import ShareForm from '../Share/ShareForm';
import DuplicateSuccessModal from '../Dialogs/DuplicateSuccessModal';
import { archiveFormAndRelatedData } from '../../utils/firestore';
import useDuplicateForm from '../../hooks/useDuplicateForm';
import { useAuth } from '../../contexts/AuthContext';

/**
 * HomeFormActionsMenu
 * -----------------------------------------------------------------------------
 * Shared Home form action menu for grid cards and list/table rows.
 *
 * PURPOSE
 *  • Keep Home form actions centralized across:
 *      - HomeCard grid view
 *      - HomeFormsTable list/table view
 *  • Preserve the same schema and permissions currently used by HomeCard.
 *  • Avoid duplicating Configure / Submissions / Share / Duplicate / Archive
 *    logic in multiple presentation components.
 *
 * ENTERPRISE RULES
 *  • No Firestore reads during render.
 *  • No screenshot resolution.
 *  • No pagination ownership.
 *  • No table/grid layout ownership.
 *  • Menu visibility and disabled states are derived from form ACL + user
 *    permissions only.
 *  • All click handlers stop propagation so table row click behavior does
 *    not accidentally fire when opening menu actions.
 *
 * EXPECTED FORM SCHEMA
 *  form.id
 *  form.title
 *  form.status
 *  form.owner
 *  form.createdBy
 *  form.organizationId
 *  form.orgAccess
 *  form.sharedWith
 *  form.isPublic
 *  form.isTemplate
 *  form.isAssetUploadForm
 *  form.assetIngestConfig
 *  form.formType
 *
 * ACTIONS
 *  Configure    → /form-builder/:formId/design
 *  Submissions  → /forms/:formId/submissions
 *  Share        → ShareForm dialog
 *  Duplicate    → useDuplicateForm + DuplicateSuccessModal
 *  Archive      → optimistic parent removal + archiveFormAndRelatedData
 * -----------------------------------------------------------------------------
 */

const DEFAULT_MENU_WIDTH = '16ch';

const stopEvent = (event) => {
  if (!event) return;

  if (typeof event.preventDefault === 'function') {
    event.preventDefault();
  }

  if (typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }
};

const getDisplayTitle = (form) => (
  form?.title ||
  form?.formDetails?.title ||
  'Untitled Form'
);

const isAssetUploadForm = (form) => (
  form?.isAssetUploadForm === true ||
  Boolean(form?.assetIngestConfig) ||
  form?.formType === 'assetUpload'
);

const resolveEffectiveRole = ({
  form,
  currentUser,
  organizationId,
}) => {
  if (!form || !currentUser) return 'none';

  /* 1️⃣  Form owner */
  if (form.owner === currentUser.uid) return 'owner';

  /* 2️⃣  Explicit ACL entry */
  if (form.sharedWith?.[currentUser.uid]) {
    return form.sharedWith[currentUser.uid]; // 'edit' | 'view'
  }

  /* 3️⃣  Org-wide access */
  if (form.organizationId === organizationId) {
    if (form.orgAccess === 'edit') return 'edit';
    if (form.orgAccess === 'view') return 'view';
  }

  return 'none';
};

const HomeFormActionsMenu = memo(function HomeFormActionsMenu({
  form,
  onDelete = null,
  iconButtonSx = null,
  menuPaperSx = null,
}) {
  const navigate = useNavigate();
  const { hasPermission, currentUser, organizationId } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [showShareForm, setShowShareForm] = useState(false);
  const archiveInFlightRef = useRef(false);

  const {
    handleDuplicateForm,
    duplicateModalState,
    goToDuplicatedForm,
    dismissDuplicateModal,
  } = useDuplicateForm();

  const formId = form?.id || null;
  const displayTitle = useMemo(() => getDisplayTitle(form), [form]);

  const formSubmissionsPath = formId
    ? `/forms/${formId}/submissions`
    : null;

  const formConfigPath = formId
    ? `/form-builder/${formId}/design`
    : null;

  const isMenuOpen = Boolean(anchorEl);
  const isAUF = useMemo(() => isAssetUploadForm(form), [form]);

  const effectiveRole = useMemo(() => (
    resolveEffectiveRole({
      form,
      currentUser,
      organizationId,
    })
  ), [
    form,
    currentUser,
    organizationId,
  ]);

  const canEdit =
    effectiveRole === 'edit' ||
    effectiveRole === 'owner';

  const canShare =
    canEdit &&
    hasPermission('shareForm');

  const canDuplicate = hasPermission('createForms');

  const canShowMenu = Boolean(formId && canEdit);

  const handleMenuOpen = useCallback((event) => {
    stopEvent(event);
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback((event) => {
    stopEvent(event);
    setAnchorEl(null);
  }, []);

  const closeMenuOnly = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleConfigure = useCallback((event) => {
    stopEvent(event);
    closeMenuOnly();

    if (!formConfigPath) return;

    navigate(formConfigPath, {
      state: { formId },
    });
  }, [
    closeMenuOnly,
    formConfigPath,
    formId,
    navigate,
  ]);

  const handleSubmissions = useCallback((event) => {
    stopEvent(event);
    closeMenuOnly();

    if (!formSubmissionsPath) return;

    navigate(formSubmissionsPath);
  }, [
    closeMenuOnly,
    formSubmissionsPath,
    navigate,
  ]);

  const handleShare = useCallback((event) => {
    stopEvent(event);

    if (!canShare) {
      console.warn(
        `[HomeFormActionsMenu] Share ignored. Missing permission for ${formId}`
      );
      return;
    }

    closeMenuOnly();
    setShowShareForm(true);
  }, [
    canShare,
    closeMenuOnly,
    formId,
  ]);

  const handleDuplicate = useCallback((event) => {
    stopEvent(event);

    if (!formId) return;

    if (!canDuplicate) {
      console.warn(
        `[HomeFormActionsMenu] Duplicate ignored. Missing permission for ${formId}`
      );
      return;
    }

    closeMenuOnly();
    handleDuplicateForm(formId);
  }, [
    canDuplicate,
    closeMenuOnly,
    formId,
    handleDuplicateForm,
  ]);

  const handleArchive = useCallback(async (event) => {
    stopEvent(event);

    if (!formId) return;

    if (archiveInFlightRef.current) {
      console.warn(
        `[HomeFormActionsMenu] Archive ignored because one is already in flight for ${formId}`
      );
      return;
    }

    closeMenuOnly();
    archiveInFlightRef.current = true;

    try {
      onDelete?.(formId);                         // ① immediate UI update

      // fire-and-forget; await only for error handling / logging
      await archiveFormAndRelatedData(formId);    // ② network
//    console.log(`[HomeFormActionsMenu] Archive confirmed for ${formId}`);

    } catch (error) {
      console.error(`Error archiving form with ID ${formId}:`, error);
    } finally {
      archiveInFlightRef.current = false;
    }
  }, [
    closeMenuOnly,
    formId,
    onDelete,
  ]);

  const handleShareDialogClose = useCallback((event) => {
    stopEvent(event);
    setShowShareForm(false);
  }, []);

  if (!canShowMenu) return null;

  return (
    <>
      <IconButton
        aria-label={`More options for ${displayTitle}`}
        aria-controls={isMenuOpen ? `home-form-actions-${formId}` : undefined}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        size="small"
        onMouseDown={(event) => {
          // Prevent table row focus/click from reacting to the action button.
          event.stopPropagation();
        }}
        onClick={handleMenuOpen}
        sx={{
          p: 0.5,
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
            color: 'text.primary',
          },
          ...(iconButtonSx || {}),
        }}
      >
        <MoreVertIcon sx={{ fontSize: '1rem' }} />
      </IconButton>

      <Menu
        id={`home-form-actions-${formId}`}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(event) => {
          // Keep menu clicks from bubbling to a table row.
          event.stopPropagation();
        }}
        PaperProps={{
          sx: {
            width: DEFAULT_MENU_WIDTH,
            maxHeight: 240,
            borderRadius: 2,
            boxShadow: 3,
            ...(menuPaperSx || {}),
          },
        }}
        MenuListProps={{
          dense: true,
          'aria-label': `Actions for ${displayTitle}`,
          sx: {
            py: 0.5,
            '& .MuiMenuItem-root': {
              fontSize: '0.8rem',
              minHeight: 32,
            },
          },
        }}
      >
        <MenuItem onClick={handleConfigure}>
          Configure
        </MenuItem>

        <MenuItem onClick={handleSubmissions}>
          Submissions
        </MenuItem>

        {!isAUF && (
          <Tooltip
            title={
              !canShare
                ? "You don't have permission to share forms"
                : ''
            }
            placement="left"
          >
            <span>
              <MenuItem
                onClick={handleShare}
                disabled={!canShare}
              >
                Share
              </MenuItem>
            </span>
          </Tooltip>
        )}

        {!isAUF && (
          <Tooltip
            title={
              !canDuplicate
                ? "You don't have permission to duplicate forms"
                : ''
            }
            placement="left"
          >
            <span>
              <MenuItem
                onClick={handleDuplicate}
                disabled={!canDuplicate}
              >
                Duplicate
              </MenuItem>
            </span>
          </Tooltip>
        )}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={handleArchive}
          sx={{
            color: 'error.main',
          }}
        >
          Archive
        </MenuItem>
      </Menu>

      <Dialog
        open={showShareForm}
        onClose={handleShareDialogClose}
        fullWidth
        maxWidth="sm"
        onClick={(event) => {
          // Keep dialog clicks from bubbling into list rows if mounted there.
          event.stopPropagation();
        }}
      >
        <Box sx={{ p: 3 }}>
          <ShareForm
            formName={displayTitle}
            formId={formId}
            formUrl={form?.status === 'published' ? `/forms/${formId}` : null}
            formStatus={form?.status}
            isPublic={form?.isPublic}
            orgAccess={form?.orgAccess}
            sharedWith={form?.sharedWith}
            onClose={handleShareDialogClose}
          />
        </Box>
      </Dialog>

      <DuplicateSuccessModal
        open={duplicateModalState.open}
        onClose={dismissDuplicateModal}
        onNavigate={goToDuplicatedForm}
      />
    </>
  );
}, areEqual);

function areEqual(prevProps, nextProps) {
  return (
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.iconButtonSx === nextProps.iconButtonSx &&
    prevProps.menuPaperSx === nextProps.menuPaperSx &&

    prevProps.form?.id === nextProps.form?.id &&
    prevProps.form?.title === nextProps.form?.title &&
    prevProps.form?.status === nextProps.form?.status &&

    // Permission / action visibility fields
    prevProps.form?.owner === nextProps.form?.owner &&
    prevProps.form?.createdBy === nextProps.form?.createdBy &&
    prevProps.form?.organizationId === nextProps.form?.organizationId &&
    prevProps.form?.orgAccess === nextProps.form?.orgAccess &&
    prevProps.form?.sharedWith === nextProps.form?.sharedWith &&
    prevProps.form?.isPublic === nextProps.form?.isPublic &&

    // AUF suppression fields
    prevProps.form?.isAssetUploadForm === nextProps.form?.isAssetUploadForm &&
    prevProps.form?.assetIngestConfig === nextProps.form?.assetIngestConfig &&
    prevProps.form?.formType === nextProps.form?.formType
  );
}

export default HomeFormActionsMenu;